import { ReceiptType } from "../../../models";

/**
 * Maps extraction confidence to a confidence band.
 * @param confidence - The confidence value (0–1) or null
 * @returns "high" (≥0.80), "med" (≥0.50), "low" (<0.50), or "none" (null)
 */
export function confidenceBand(
  confidence: number | null
): "high" | "med" | "low" | "none" {
  if (confidence === null) return "none";
  if (confidence >= 0.8) return "high";
  if (confidence >= 0.5) return "med";
  return "low";
}

/**
 * Formats a number as USD currency string.
 * @param amount - The amount in dollars or null
 * @returns Formatted string like "$45.00" or "—" for null
 */
export function formatMoney(amount: number | null): string {
  if (amount === null) return "—";
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Returns a human-readable label for a receipt type.
 * @param type - The receipt type ("gas" | "misc")
 * @returns Human-readable label
 */
export function receiptTypeLabel(type: ReceiptType): string {
  const labels: Record<ReceiptType, string> = {
    gas: "Gas",
    misc: "Misc",
  };
  return labels[type] ?? type;
}

/**
 * Returns a formatted date string for display, preferring receiptDate over createdAt.
 *
 * The receipt date is a calendar date (either "2026-10-01" or a midnight-UTC
 * "2026-10-20T00:00:00Z"). Parsing it with `new Date()` treats it as a UTC
 * instant, so `toLocaleDateString()` in a US timezone shifts it to the previous
 * day. Instead we read the YYYY-MM-DD portion and format it as a local calendar
 * date, so the displayed day always matches the stored date.
 *
 * @param receiptDate - ISO date string or null
 * @param createdAt - ISO date string (fallback)
 */
export function formatReceiptDate(
  receiptDate: string | null,
  createdAt: string
): string {
  const raw = receiptDate ?? createdAt;
  const [year, month, day] = raw.slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
}
